import React, { useEffect, useState } from "react";
import Modal from "../../../common/Modal";
import TextInput from "../../../common/TextInput";
// import SelectInput from "../../../common/SelectInput";
import Button from "../../../common/Buttons";

import {
  validateRequired,
  validateText,
  validateCatalog,
  validatePositiveNumber,
  validatePositiveInteger,
  validateOptionalText,
} from "../../../utils/Constant";
import SearchableSelectInput from "../../../common/SearchableSelectInput";

const EMPTY_FORM = {
  supplier: "",
  item: "",
  type: "",
  customType: "",
  catalog: "",
  manufacturer: "",
  unitSize: "",
  numUnits: "",
  unitPrice: "",
  notes: "",
};

export default function RequestFormModal({
  open,
  mode,
  initialValue,
  suppliers = [],
  types = [],
  onClose,
  onSubmit,
  loading = false,
}) {
  const [form, setForm] = useState({
    ...EMPTY_FORM,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) {
      return;
    }

    if (mode === "edit" && initialValue) {
      const isExistingType = types.includes(initialValue.type);

      setForm({
        ...EMPTY_FORM,
        ...initialValue,

        type: isExistingType
          ? initialValue.type
          : initialValue.type
            ? "Custom"
            : "",

        customType: isExistingType ? "" : initialValue.type || "",

        unitSize:
          initialValue.unitSize !== undefined && initialValue.unitSize !== null
            ? String(initialValue.unitSize)
            : "",

        numUnits:
          initialValue.numUnits !== undefined && initialValue.numUnits !== null
            ? String(initialValue.numUnits)
            : "",

        unitPrice:
          initialValue.unitPrice !== undefined &&
          initialValue.unitPrice !== null
            ? String(initialValue.unitPrice)
            : "",
      });
    } else {
      setForm({ ...EMPTY_FORM });
    }
    setErrors({});
  }, [open, mode, initialValue, types]);

  const handleFieldChange = (fieldName) => (event) => {
    const { value } = event.target;

    setForm((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [fieldName]: "",
    }));

    if (fieldName === "type" && value !== "Custom") {
      setForm((prev) => ({
        ...prev,
        customType: "",
      }));

      setErrors((prev) => ({
        ...prev,
        customType: "",
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    newErrors.supplier = validateRequired(form.supplier, "Supplier");

    newErrors.item = validateText(form.item, "Item name");

    newErrors.type = validateRequired(form.type, "Type");

    if (form.type === "Custom") {
      newErrors.customType = validateText(form.customType, "Custom type");
    }

    newErrors.catalog = validateCatalog(form.catalog, "Catalog");

    newErrors.manufacturer = validateText(form.manufacturer, "Manufacturer");

    newErrors.unitSize = validatePositiveNumber(form.unitSize, "Unit size");

    newErrors.numUnits = validatePositiveInteger(
      form.numUnits,
      "Number of units",
    );

    newErrors.unitPrice = validatePositiveNumber(form.unitPrice, "Unit price");

    newErrors.notes = validateOptionalText(form.notes, "Notes", 500);

    Object.keys(newErrors).forEach((key) => {
      if (!newErrors[key]) {
        delete newErrors[key];
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    const isValid = validate();

    if (!isValid) {
      return;
    }

    const payload = {
      supplier: form.supplier.trim(),
      item: form.item.trim(),
      type: form.type === "Custom" ? form.customType.trim() : form.type,
      catalog: form.catalog.trim(),
      manufacturer: form.manufacturer.trim(),
      unitSize: Number(form.unitSize),
      numUnits: Number(form.numUnits),
      unitPrice: Number(form.unitPrice),
      notes: form.notes.trim(),
      ...(mode === "add" && {
        status: "new",
      }),
    };

    onSubmit(payload);
  };

  const supplierOptions = suppliers.map((supplier) => ({
    value: supplier,
    label: supplier,
  }));

  const typeOptions = [...types, "Custom"].map((type) => ({
    value: type,
    label: type,
  }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === "edit" ? "Edit Request" : "Add Request"}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button type="button" onClick={handleSubmit} disabled={loading}>
            {loading
              ? "Saving..."
              : mode === "edit"
                ? "Save Changes"
                : "Add Request"}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {/* Supplier */}
        <div className="sm:col-span-2">
          {/* <SelectInput
            name="supplier"
            label="Supplier"
            required
            options={supplierOptions}
            placeholder="Select supplier"
            value={form.supplier}
            onChange={handleFieldChange("supplier")}
            error={errors.supplier}
            disabled={loading}
          /> */}

          <SearchableSelectInput
            name="supplier"
            label="Supplier"
            required
            options={supplierOptions}
            placeholder="Select supplier"
            searchPlaceholder="Search supplier..."
            value={form.supplier}
            onChange={handleFieldChange("supplier")}
            error={errors.supplier}
            disabled={loading}
          />
        </div>

        {/* Item */}
        <div className="sm:col-span-2">
          <TextInput
            label="Item Name"
            required
            placeholder="Item name"
            value={form.item}
            onChange={handleFieldChange("item")}
            error={errors.item}
            disabled={loading}
          />
        </div>

        {/* Type */}

           <SearchableSelectInput
            name="type"
            label="Type"
            required
            options={typeOptions}
            placeholder="Select type"
            searchPlaceholder="Search type..."
            value={form.type}
            onChange={handleFieldChange("type")}
            error={errors.type}
            disabled={loading}
          />

        {/* Custom Type */}
        {form.type === "Custom" && (
          <TextInput
            label="Custom Type"
            required
            placeholder="Enter custom type"
            value={form.customType}
            onChange={handleFieldChange("customType")}
            error={errors.customType}
            disabled={loading}
          />
        )}

        {/* Catalog */}
        <TextInput
          label="Catalog"
          required
          placeholder="Catalog"
          value={form.catalog}
          onChange={handleFieldChange("catalog")}
          error={errors.catalog}
          disabled={loading}
        />

        {/* Manufacturer */}
        <TextInput
          label="Manufacturer"
          required
          placeholder="Manufacturer"
          value={form.manufacturer}
          onChange={handleFieldChange("manufacturer")}
          error={errors.manufacturer}
          disabled={loading}
        />

        {/* Unit Size */}
        <TextInput
          label="Unit Size"
          required
          type="number"
          placeholder="0"
          value={form.unitSize}
          onChange={handleFieldChange("unitSize")}
          error={errors.unitSize}
          disabled={loading}
        />

        {/* Number of Units */}
        <TextInput
          label="Number of Units"
          required
          type="number"
          placeholder="0"
          value={form.numUnits}
          onChange={handleFieldChange("numUnits")}
          error={errors.numUnits}
          disabled={loading}
        />

        {/* Unit Price */}
        <TextInput
          label="Unit Price (₹)"
          required
          type="number"
          placeholder="0"
          value={form.unitPrice}
          onChange={handleFieldChange("unitPrice")}
          error={errors.unitPrice}
          disabled={loading}
        />

        {/* Notes */}
        <div className="sm:col-span-2">
          <TextInput
            label="Notes"
            placeholder="Additional notes"
            value={form.notes}
            onChange={handleFieldChange("notes")}
            error={errors.notes}
            disabled={loading}
          />
        </div>
      </div>
    </Modal>
  );
}
